import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core'
import { ComboOption, ComboRow, exactMatch, filterOptions, sameOptions } from 'src/app/domain/combobox'

export { ComboOption } from 'src/app/domain/combobox'

let nextId = 0

/**
 * A dropdown you can also type into. The chevron lists every option; typing narrows the list and highlights the match.
 * Picking a row emits (picked). With allowNew, text that matches nothing offers an "Add new …" row and emits (typed)
 * with the text when it is chosen or the field is left; without it, unmatched text reverts to the current pick on blur.
 * Not a form control on purpose: the page owns the id and the free text and patches its own form.
 */
@Component({
  selector: 'rt-combobox',
  template: `
    <div class="rt-combo" [class.is-open]="open" [class.is-disabled]="disabled" [class.is-locked]="locked" [class.is-plain]="!hasList">
      <input #field class="rt-fill rt-combo__input" [class.is-missing]="missing" type="text" [id]="inputId" [value]="text"
             [placeholder]="placeholder" [disabled]="disabled" [readonly]="locked" [attr.aria-label]="label || null"
             role="combobox" aria-autocomplete="list" [attr.aria-expanded]="open" [attr.aria-controls]="listId"
             autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" [attr.maxlength]="maxlength"
             (input)="onInput($any($event.target).value)" (focus)="onFocus()" (blur)="onBlur()" (keydown)="onKey($event)" />
      <button *ngIf="!disabled && hasList" [disabled]="locked" type="button" class="rt-combo__chev" tabindex="-1" [attr.aria-label]="'Show all ' + (newLabel ? newLabel + 's' : 'options')"
              (mousedown)="$event.preventDefault()" (click)="toggle()">
      </button>
      <ul *ngIf="open" class="rt-combo__list" role="listbox" [id]="listId" (mousedown)="$event.preventDefault()">
        <li *ngFor="let r of rows; let i = index; trackBy: trackRow" class="rt-combo__row" role="option" [class.is-active]="i === active" [attr.aria-selected]="i === active"
            (mouseenter)="active = i" (click)="pick(r.option)">
          <span class="rt-combo__name">{{ r.pre }}<mark *ngIf="r.hit">{{ r.hit }}</mark>{{ r.post }}</span>
          <span *ngIf="r.option.sub" class="rt-combo__sub">{{ r.option.sub }}</span>
        </li>
        <li *ngIf="newRow" class="rt-combo__row rt-combo__row--new" role="option" [class.is-active]="active === rows.length" [attr.aria-selected]="active === rows.length"
            (mouseenter)="active = rows.length" (click)="addNew()">+ Add new {{ newLabel }} “{{ text.trim() }}”</li>
        <li *ngIf="!rows.length && !newRow" class="rt-combo__row rt-combo__row--none">{{ emptyText }}</li>
      </ul>
    </div>`,
})
export class RtComboboxComponent implements OnChanges {
  @Input() options: readonly ComboOption[] = []
  /** The picked option's id, or '' / null for none. */
  @Input() value: number | string | null = ''
  /** Free text to show when nothing is picked (a new location or unit name already typed). */
  @Input() freeText = ''
  @Input() placeholder = 'Select or type…'
  @Input() label = ''
  @Input() inputId = `rt-combo-${++nextId}`
  @Input() disabled = false
  /** Not pickable yet (no customer chosen): still drawn as a control, but inert. disabled is the read-only plain-text state. */
  @Input() locked = false
  @Input() missing = false
  @Input() maxlength = 80
  /** Unmatched text may become a new entry named by newLabel ("location", "unit"). */
  @Input() allowNew = false
  @Input() newLabel = ''
  /** Clear the field after a pick (the crew adder). */
  @Input() clearOnPick = false
  @Input() emptyText = 'No matches'

  @Output() picked = new EventEmitter<ComboOption>()
  @Output() typed = new EventEmitter<string>()

  @ViewChild('field') field?: ElementRef<HTMLInputElement>

  text = ''
  open = false
  rows: ComboRow[] = []
  active = -1
  /** False while the chevron shows the whole list regardless of the text. */
  private filtering = true
  get listId(): string { return `${this.inputId}-list` }

  trackRow(_: number, r: ComboRow): number | string { return r.option.id }

  /** A parent that rebuilds its options array every check must not reset the open list under a tap. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && !changes['options'].firstChange && sameOptions(changes['options'].previousValue, this.options)) delete changes['options']
    if (changes['value'] || changes['freeText'] || changes['options']) {
      if (!this.open || changes['value']) this.text = this.pickedName() ?? this.freeText ?? ''
      if (this.open) this.refresh()
    }
  }

  private pickedName(): string | null {
    const v = this.value
    if (v === '' || v === null || v === undefined) return null
    return this.options.find(o => String(o.id) === String(v))?.name ?? null
  }

  /** Plain free text (a unit on a brand-new location) has nothing to list, so no chevron or popup. */
  get hasList(): boolean {
    return this.locked || this.options.length > 0 || !this.allowNew
  }

  get newRow(): boolean {
    return this.allowNew && !!this.text.trim() && !exactMatch(this.options, this.text)
  }

  private refresh() {
    this.rows = filterOptions(this.options, this.filtering ? this.text : '')
    const n = this.rows.length + (this.newRow ? 1 : 0)
    this.active = n === 0 ? -1 : Math.min(Math.max(this.active, this.rows.length && this.filtering && this.text.trim() ? 0 : -1), n - 1)
  }

  private show(filtering: boolean) {
    if (this.disabled || this.locked) return
    this.filtering = filtering
    this.open = true
    this.active = -1
    this.refresh()
  }

  private hide() {
    this.open = false
    this.active = -1
  }

  onInput(text: string) {
    this.text = text
    if (this.hasList) this.show(true)
  }

  onFocus() {
    if (this.hasList) this.show(this.text.trim() !== '' && !this.pickedName())
  }

  toggle() {
    if (this.open && !this.filtering) this.hide()
    else { this.show(false); this.field?.nativeElement.focus() }
  }

  onBlur() {
    this.hide()
    this.commitText()
  }

  /** Leaving the field: exact text is a pick; otherwise new text (allowNew) or a revert to the current pick. */
  private commitText() {
    const exact = exactMatch(this.options, this.text)
    if (exact && String(exact.id) !== String(this.value ?? '')) { this.pick(exact); return }
    if (exact) { this.text = exact.name; return }
    const t = this.text.trim()
    if (this.allowNew) {
      if (t !== (this.freeText ?? '').trim() || this.pickedName()) this.typed.emit(t)
      this.text = t
    } else {
      this.text = this.clearOnPick ? '' : this.pickedName() ?? ''
    }
  }

  pick(option: ComboOption) {
    this.text = this.clearOnPick ? '' : option.name
    this.hide()
    this.picked.emit(option)
  }

  addNew() {
    const t = this.text.trim()
    this.text = t
    this.hide()
    this.typed.emit(t)
  }

  onKey(e: KeyboardEvent) {
    if (this.locked) { if (e.key !== 'Tab') e.preventDefault(); return }
    const n = this.rows.length + (this.newRow ? 1 : 0)
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!this.open) this.show(false)
      else if (n) this.active = (this.active + 1) % n
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (this.open && n) this.active = (this.active - 1 + n) % n
    } else if (e.key === 'Enter') {
      if (!this.open) return
      e.preventDefault()
      if (this.active >= 0 && this.active < this.rows.length) this.pick(this.rows[this.active].option)
      else if (this.active === this.rows.length && this.newRow) this.addNew()
      else if (this.rows.length === 1) this.pick(this.rows[0].option)
      else if (this.newRow) this.addNew()
    } else if (e.key === 'Escape') {
      if (this.open) { e.preventDefault(); this.hide() }
    } else if (e.key === 'Tab') {
      this.hide()
    }
  }
}
